import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { PrimaryButton, SecondaryButton, Input } from "../components/ui-bits";
import { ShippingContainerIcon, MicrosoftOutlookLogoIcon, ArrowLeftIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, verifyMfa, ssoStart, ssoCallback, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [ttl, setTtl] = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ssoCode = params.get("code");
    const state = params.get("state");
    if (ssoCode) {
      setLoading(true);
      ssoCallback(ssoCode, state)
        .then((u) => {
          toast.success("Signed in via Microsoft");
          navigate(u?.must_change_password ? "/change-password" : "/", { replace: true });
        })
        .catch((e) => {
          toast.error(e?.response?.data?.detail || "SSO failed");
          setLoading(false);
        });
    }
  }, [location.search, ssoCallback, navigate]);

  useEffect(() => {
    if (user) navigate(user.must_change_password ? "/change-password" : "/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfa_required) {
        setChallengeId(res.challenge_id);
        setTtl(res.ttl_minutes);
        setStep("mfa");
        toast.success(`Code sent to ${email}`);
      } else if (res.user) {
        toast.success("Signed in");
        navigate(res.user.must_change_password ? "/change-password" : "/", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verifyMfa(challengeId, code);
      toast.success("Signed in");
      navigate(u.must_change_password ? "/change-password" : "/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md border border-zinc-200 bg-white p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-zinc-950 text-white flex items-center justify-center">
            <ShippingContainerIcon size={26} weight="bold" />
          </div>
          <div>
            <div className="font-heading font-black text-xl tracking-tight">CARGO/SCAN</div>
            <div className="overline mt-0.5">
              {step === "credentials" ? "Sign in" : "Verify your identity"}
            </div>
          </div>
        </div>

        {step === "credentials" && (
          <>
            <PrimaryButton
              type="button"
              onClick={ssoStart}
              data-testid="sso-btn"
              className="w-full flex items-center justify-center gap-2 mb-3 !bg-[#0078D4] hover:!bg-[#106EBE]"
            >
              <MicrosoftOutlookLogoIcon size={18} weight="bold" />
              Sign in with Microsoft
            </PrimaryButton>

            <div className="flex items-center gap-3 my-4">
              <hr className="flex-1 border-zinc-200" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">OR</span>
              <hr className="flex-1 border-zinc-200" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Email</label>
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full" placeholder="you@company.com" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Password (or PUK)</label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full" />
              </div>
              <PrimaryButton type="submit" data-testid="login-submit" disabled={loading} className="w-full">
                {loading ? "Signing in…" : "Continue"}
              </PrimaryButton>
              <a href="/forgot-password" className="block text-xs text-zinc-500 hover:text-zinc-950 mt-1" data-testid="forgot-link">
                Forgot password?
              </a>
            </form>
          </>
        )}

        {step === "mfa" && (
          <form onSubmit={onVerify} className="space-y-4">
            <p className="text-sm text-zinc-600">
              We sent a 6-digit code to <strong className="text-zinc-950">{email}</strong>. It expires in {ttl} minutes.
            </p>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Code</label>
              <Input
                data-testid="mfa-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                inputMode="numeric"
                maxLength={6}
                className="w-full font-mono-num text-2xl tracking-[0.4em] text-center"
                placeholder="000000"
              />
            </div>
            <PrimaryButton type="submit" data-testid="mfa-submit" disabled={loading || code.length !== 6} className="w-full">
              {loading ? "Verifying…" : "Verify & sign in"}
            </PrimaryButton>
            <button
              type="button"
              onClick={() => { setStep("credentials"); setCode(""); setChallengeId(""); }}
              className="text-xs text-zinc-500 hover:text-zinc-950 flex items-center gap-1"
            >
              <ArrowLeftIcon size={12} /> Use a different account
            </button>
          </form>
        )}

        <p className="text-[10px] text-zinc-400 mt-6 text-center leading-relaxed">
          Personal mode · single-user private app. Use your email + password or PUK. Microsoft SSO is optional. Forgotten password? Click the link above for an email reset.
        </p>
      </div>
    </div>
  );
}
